import { Subscription } from "@/types/subscription";
import { getLocalSubscriptions, saveLocalSubscriptions } from "@/utils/localStorageUtils";
import { checkSupabaseClient } from "@/utils/supabaseUtils";
import { calculatePaymentsDue } from "@/utils/paymentUtils";
import { fetchSubscriptionsFromSupabase, createSubscriptionInSupabase } from "../core/subscriptionApi";
import { prepareSubscriptionForSupabaseData, createLocalSubscription } from "../utils/subscriptionUtils";

export const fetchAllSubscriptions = async (): Promise<Subscription[]> => {
  console.log("fetchAllSubscriptions: Starting fetch operation");
  
  try {
    const clientExists = checkSupabaseClient();
    console.log("fetchAllSubscriptions: Supabase client check:", clientExists);
    
    if (clientExists) {
      try {
        const subscriptions = await fetchSubscriptionsFromSupabase();
        if (subscriptions.length > 0) {
          // Return ALL subscriptions (including cancelled ones) for admin view
          console.log("fetchAllSubscriptions: Returning all subscriptions:", subscriptions);
          return subscriptions;
        }
        console.log("fetchAllSubscriptions: No data returned from Supabase, using local storage");
      } catch (error) {
        console.warn("fetchAllSubscriptions: Supabase fetch failed, using local storage", error);
      }
    }
    
    console.log("fetchAllSubscriptions: Getting data from local storage");
    const subscriptions = getLocalSubscriptions();
    // Return all subscriptions from local storage too
    console.log("fetchAllSubscriptions: Local storage all subscriptions:", subscriptions);
    await new Promise(resolve => setTimeout(resolve, 300));
    return subscriptions;
  } catch (error) {
    console.error("fetchAllSubscriptions: Failed to fetch subscriptions:", error);
    const subscriptions = getLocalSubscriptions();
    // Return all subscriptions even in error case
    console.log("fetchAllSubscriptions: Error fallback - using all local storage:", subscriptions);
    return subscriptions;
  }
};

export const createNewSubscription = async (data: Omit<Subscription, "id" | "totalPaid">): Promise<Subscription> => {
  console.log("=== CREATE NEW SUBSCRIPTION - ENTRY POINT DEBUG ===");
  console.log("createNewSubscription called with data:", JSON.stringify(data, null, 2));
  console.log("Business fields in input:", {
    isBusiness: data.isBusiness,
    businessName: data.businessName,
    isBusinessType: typeof data.isBusiness,
    businessNameType: typeof data.businessName
  });
  
  try {
    const clientExists = checkSupabaseClient();
    console.log("Supabase client exists:", clientExists);
    
    if (clientExists) {
      try {
        console.log("Attempting to create subscription in Supabase...");
        const supabaseData = prepareSubscriptionForSupabaseData(data);
        console.log("Prepared Supabase data:", JSON.stringify(supabaseData, null, 2));
        
        const newSubscription = await createSubscriptionInSupabase(supabaseData);
        console.log("Successfully created subscription in Supabase:", newSubscription);
        
        // Also save to local storage for backup
        const localSubscriptions = getLocalSubscriptions();
        localSubscriptions.push(newSubscription);
        saveLocalSubscriptions(localSubscriptions);
        console.log("Also saved to local storage as backup");
        
        return newSubscription;
      } catch (supabaseError) {
        console.error("Failed to create subscription in Supabase:", supabaseError);
        console.error("Error details:", {
          message: supabaseError.message,
          stack: supabaseError.stack,
          name: supabaseError.name
        });
        console.log("Falling back to local storage");
        // Continue to local storage fallback
      }
    } else {
      console.warn("Supabase client not available, using local storage only");
    }
    
    // Local storage fallback
    console.log("Creating subscription in local storage only");
    const subscriptions = getLocalSubscriptions();
    const newSubscription = createLocalSubscription(data);
    
    subscriptions.push(newSubscription);
    saveLocalSubscriptions(subscriptions);
    console.log("Created subscription in local storage:", newSubscription);
    return newSubscription;
  } catch (error) {
    console.error("Failed to create subscription:", error);
    throw new Error("Failed to create subscription: " + (error instanceof Error ? error.message : String(error)));
  }
};

export const processSubscriptionPayments = async (): Promise<{ message: string }> => {
  console.log("processSubscriptionPayments called");
  
  try {
    const subscriptions = getLocalSubscriptions();
    const updatedSubscriptions = calculatePaymentsDue(subscriptions);
    
    const paymentCount = updatedSubscriptions.reduce((count, sub, index) => {
      return count + (sub.totalPaid > subscriptions[index].totalPaid ? 1 : 0);
    }, 0);
    
    saveLocalSubscriptions(updatedSubscriptions);
    console.log(`Processed payments for ${paymentCount} subscriptions`);
    return { message: `Processed payments for ${paymentCount} subscriptions` };
  } catch (error) {
    console.error("Failed to process payments:", error);
    throw new Error("Failed to process payments");
  }
};
